import React, { useEffect, useState, useRef } from 'react';
import { CardHeader, InputAdornment, OutlinedInput } from '@material-ui/core';
import clsx from 'clsx';
import { Form, Formik } from 'formik';
import { useGlobal } from '@rcg/ui-react';
import useStyles from '../useStyles';
import useStylesCheckout from '../../CheckoutCard/useStyles';
import Dictionary from '../../../utils/dictionary';
import { emptyFunction } from '../../../utils/common';
import InfoTooltip from '../../InfoTooltip';

const CustomErrorMessage = ({ message, className }) => {
  const classes = useStyles();
  return <div className={clsx(classes.errorMessage, className)}>{message}</div>;
};

const CustomInput = ({
  name,
  label,
  placeholder,
  value,
  onChange,
  icon = null,
  labelInfoIcon = false,
  className,
  error,
  errorMessage,
  inputProps = {},
  onBlur,
  disabled = false,
  tooltipTitle = '',
  tooltipDescription = '',
  tooltipOffsetLeft = '0',
}) => {
  const classes = useStyles();
  const classesCheckout = useStylesCheckout();
  const [inputValue, setInputValue] = useState(value);
  const iconAdornment = icon ? <InputAdornment position="end">{icon}</InputAdornment> : null;
  const handleChange = (event) => {
    setInputValue(event.target.value);
    onChange(event);
  };
  useEffect(() => {
    setInputValue(value);
  }, [value]);
  return (
    <div
      className={clsx(classes.inputWrapper, 'inputWrapper', className, error ? 'input-error' : '')}>
      <div className={classes.inputLabel}>
        {label}{' '}
        {labelInfoIcon && (
          <InfoTooltip
            title={tooltipTitle}
            description={tooltipDescription}
            tooltipOffsetLeft={tooltipOffsetLeft}
          />
        )}
      </div>
      <div className={clsx(classesCheckout.inputBox, classes.inputBox, 'inputBox')}>
        <OutlinedInput
          id={name}
          name={name}
          variant="outlined"
          fullWidth
          placeholder={placeholder}
          endAdornment={iconAdornment}
          value={inputValue || ''}
          onChange={handleChange}
          inputProps={inputProps}
          onBlur={onBlur}
          disabled={disabled}
        />
      </div>
      {error ? <CustomErrorMessage message={errorMessage} /> : ''}
    </div>
  );
};
const PassengerDetailForm = (props) => {
  const classes = useStyles();
  const classesCheckout = useStylesCheckout();

  const { renderContent } = useGlobal();
  const {
    index,
    passengerInfo = {
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      knownTravelerNumber: '',
      redressNumber: '',
      loyaltyPrograms: [],
    },
    currentValidationSchema,
    onSubmit = () => {},
    handleSubmitForm = emptyFunction,
    handleCtaDisable = emptyFunction,
    passengerDetailsFormRef = null,
  } = props;

  const formRef = useRef();

  useEffect(() => {
    handleSubmitForm(formRef);
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (formRef.current) {
      formRef.current.handleSubmit();
    }
  }, []);

  const getCapitalizedLabel = (label) => {
    return label && label.charAt(0).toUpperCase() + label.slice(1).toLowerCase();
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={passengerInfo}
        validationSchema={currentValidationSchema}
        innerRef={(fmRef) => {
          formRef.current = fmRef;
          if (passengerDetailsFormRef) {
            passengerDetailsFormRef.current = fmRef;
          }
        }}
        onSubmit={(values, { setSubmitting }) => {
          onSubmit(values);
          setSubmitting(false);
        }}
        validate={(values) => {
          const errors = {};
          if (values.email) {
            const emailRegex =
              /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
            if (!emailRegex.test(values.email)) {
              errors.email = renderContent(
                Dictionary.airPassengerInfoEmailInvalidLabel.key,
                Dictionary.airPassengerInfoEmailInvalidLabel.defaultValue,
              );
            }
          }
          if (values.middleName) {
            const middleNameRegex = /^[a-zA-Z\s]*$/;
            if (!middleNameRegex.test(values.middleName)) {
              errors.middleName = renderContent(
                Dictionary.airPassengerInfoMiddleNameInvalidLabel.key,
                Dictionary.airPassengerInfoMiddleNameInvalidLabel.defaultValue,
              );
            }
          }
          return errors;
        }}>
        {({ errors, touched, values, handleSubmit, handleBlur, setFieldValue, isValid }) => {
          handleCtaDisable(isValid, values);
          return (
            <Form
              id="submit-passenger-form"
              data-testid="passengerForm"
              onSubmit={(e) => {
                e.preventDefault();
                handleSubmit();
              }}>
              <div className={clsx(classesCheckout.inputBoxWrapper, classes.inputboxWrapper)}>
                <CustomInput
                  id="first-name"
                  name="firstName"
                  className={classes.firstNameInput}
                  label={renderContent(
                    Dictionary.airPassengerInfoFirstNameLabel.key,
                    Dictionary.airPassengerInfoFirstNameLabel.defaultValue,
                  )}
                  placeholder={renderContent(
                    Dictionary.airPassengerInfoFirstNamePlaceholder.key,
                    Dictionary.airPassengerInfoFirstNamePlaceholder.defaultValue,
                  )}
                  value={getCapitalizedLabel(values.firstName)}
                  onChange={(event) => setFieldValue('firstName', event.target.value)}
                  error={errors.firstName && touched.firstName}
                  errorMessage={errors.firstName}
                  inputProps={{
                    'data-testid': 'first-name-passenger-details',
                  }}
                  onBlur={(event) => {
                    handleBlur(event);
                    onSubmit(values);
                  }}
                  disabled
                />
                <CustomInput
                  id="last-name"
                  name="lastName"
                  className={classes.lastNameInput}
                  label={renderContent(
                    Dictionary.airPassengerInfoLastNameLabel.key,
                    Dictionary.airPassengerInfoLastNameLabel.defaultValue,
                  )}
                  placeholder={renderContent(
                    Dictionary.airPassengerInfoLastNamePlaceholder.key,
                    Dictionary.airPassengerInfoLastNamePlaceholder.defaultValue,
                  )}
                  value={getCapitalizedLabel(values.lastName)}
                  onChange={(event) => setFieldValue('lastName', event.target.value)}
                  error={errors.lastName && touched.lastName}
                  errorMessage={errors.lastName}
                  inputProps={{
                    'data-testid': 'last-name-passenger-details',
                  }}
                  onBlur={(event) => {
                    handleBlur(event);
                    onSubmit(values);
                  }}
                  disabled
                />
                {index === 0 && (
                  <CustomInput
                    id="email"
                    name="email"
                    className={classes.emailInput}
                    label={renderContent(
                      Dictionary.airPassengerInfoEmailLabel.key,
                      Dictionary.airPassengerInfoEmailLabel.defaultValue,
                    )}
                    placeholder={renderContent(
                      Dictionary.airPassengerInfoEmailPlaceholder.key,
                      Dictionary.airPassengerInfoEmailPlaceholder.defaultValue,
                    )}
                    value={values.email}
                    onChange={(event) => setFieldValue('email', event.target.value)}
                    error={errors.email && touched.email}
                    errorMessage={errors.email}
                    inputProps={{
                      'data-testid': 'email',
                    }}
                    onBlur={(event) => {
                      handleBlur(event);
                      handleSubmit();
                    }}
                  />
                )}
                <CustomInput
                  id="known-traveler-number"
                  name="knownTravelerNumber"
                  className={clsx(classes.travelerNumberInput, {
                    [classes.travelerNumberInputSecond]: index !== 0,
                  })}
                  label={renderContent(
                    Dictionary.airPassengerInfoKnownTravelerLabel.key,
                    Dictionary.airPassengerInfoKnownTravelerLabel.defaultValue,
                  )}
                  labelInfoIcon
                  placeholder={renderContent(
                    Dictionary.airPassengerInfoTravelerNumberPlaceholder.key,
                    Dictionary.airPassengerInfoTravelerNumberPlaceholder.defaultValue,
                  )}
                  value={values.knownTravelerNumber}
                  onChange={(event) => setFieldValue('knownTravelerNumber', event.target.value)}
                  inputProps={{
                    'data-testid': 'known-traveler-number',
                  }}
                  onBlur={(event) => {
                    handleBlur(event);
                    onSubmit(values);
                  }}
                  tooltipTitle={renderContent(
                    Dictionary.airWizardKnownTravelerNumberTooltipTitle.key,
                    Dictionary.airWizardKnownTravelerNumberTooltipTitle.defaultValue,
                  )}
                  tooltipDescription={renderContent(
                    Dictionary.airWizardKnownTravelerNumberTooltipDescription.key,
                    Dictionary.airWizardKnownTravelerNumberTooltipDescription.defaultValue,
                  )}
                />
                <CustomInput
                  id="redress-number"
                  name="redressNumber"
                  className={clsx(classes.redressNumberInput, {
                    [classes.redressNumberInputSecond]: index !== 0,
                  })}
                  label={renderContent(
                    Dictionary.airPassengerInfoRedressNumberLabel.key,
                    Dictionary.airPassengerInfoRedressNumberLabel.defaultValue,
                  )}
                  labelInfoIcon
                  placeholder={renderContent(
                    Dictionary.airPassengerInfoRedressNumberPlaceholder.key,
                    Dictionary.airPassengerInfoRedressNumberPlaceholder.defaultValue,
                  )}
                  value={values.redressNumber}
                  onChange={(event) => setFieldValue('redressNumber', event.target.value)}
                  inputProps={{
                    'data-testid': 'redress-number',
                  }}
                  onBlur={(event) => {
                    handleBlur(event);
                    onSubmit(values);
                  }}
                  tooltipTitle={renderContent(
                    Dictionary.airWizardRedressNumberTooltipTitle.key,
                    Dictionary.airWizardRedressNumberTooltipTitle.defaultValue,
                  )}
                  tooltipDescription={renderContent(
                    Dictionary.airWizardRedressNumberTooltipDescription.key,
                    Dictionary.airWizardRedressNumberTooltipDescription.defaultValue,
                  )}
                  tooltipOffsetLeft="30"
                />
              </div>
              <CardHeader
                title={renderContent(
                  Dictionary.airFrequentFlyerTitle.key,
                  Dictionary.airFrequentFlyerTitle.defaultValue,
                )}
                classes={{
                  root: classes.wizardHeaderRoot,
                  title: clsx(
                    classes.wizardTitle,
                    classes.wizardSubtitle,
                    classes.frequentFlyerTitle,
                  ),
                }}
              />
              {/* <Divider /> */}
              {values.loyaltyPrograms.map((loyaltyProgram, loyaltyProgramIndex) => {
                return (
                  <div
                    className={clsx(classesCheckout.inputBoxWrapper, classes.frequentFlyerWrapper)}
                    key={`loyalty-program-${loyaltyProgram.frequentFlyerProgramCode}`}>
                    <CustomInput
                      id="frequent-flyer-program"
                      name="frequentFlyerProgramName"
                      className={classes.frequentFlyerInputs}
                      label={renderContent(
                        Dictionary.airPassengerInfoFrequentFlyerProgramLabel.key,
                        Dictionary.airPassengerInfoFrequentFlyerProgramLabel.defaultValue,
                      )}
                      placeholder={renderContent(
                        Dictionary.airPassengerInfoFrequentFlyerPgPlaceholder.key,
                        Dictionary.airPassengerInfoFrequentFlyerPgPlaceholder.defaultValue,
                      )}
                      value={loyaltyProgram.frequentFlyerProgramName}
                      inputProps={{
                        'data-testid': `frequent-flyer-program-${loyaltyProgram.frequentFlyerProgramCode}`,
                      }}
                      onBlur={handleBlur}
                      disabled
                    />
                    <CustomInput
                      id="frequent-flyer-number"
                      name="frequentFlyerNumber"
                      className={clsx(
                        classes.frequentFlyerInputs,
                        classes.frequentFlyerNumberInput,
                      )}
                      label={renderContent(
                        Dictionary.airPassengerInfoFrequentFlyerNumberLabel.key,
                        Dictionary.airPassengerInfoFrequentFlyerNumberLabel.defaultValue,
                      )}
                      placeholder={renderContent(
                        Dictionary.airPassengerInfoFrequentFlyerNumberPlaceholder.key,
                        Dictionary.airPassengerInfoFrequentFlyerNumberPlaceholder.defaultValue,
                      )}
                      value={loyaltyProgram.frequentFlyerNumber}
                      onChange={(event) =>
                        setFieldValue(
                          `loyaltyPrograms[${loyaltyProgramIndex}].frequentFlyerNumber`,
                          event.target.value,
                        )
                      }
                      inputProps={{
                        'data-testid': `frequent-flyer-number-${loyaltyProgram.frequentFlyerProgramCode}`,
                      }}
                      onBlur={(event) => {
                        handleBlur(event);
                        onSubmit(values);
                      }}
                    />
                  </div>
                );
              })}
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default PassengerDetailForm;
